import { useMemo, useState } from 'react';
import { Plus, Trash2, Archive, MessageSquare } from 'lucide-react';
import {
  DataTable,
  EmptyState,
  StatusPill,
  type ColumnDef,
} from '@vnetwork/ui/data';
import {
  Button,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerBody,
  DrawerFooter,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Textarea,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { Toolbar } from '@vnetwork/ui/layout';
import {
  useContractProcesses,
  useCreateContractProcess,
  useDeleteContractProcess,
  useArchiveContractProcess,
  useDeliverContractProcess,
  type ContractProcessRow,
} from '@/api/contracts';
import { formatDateTime } from '@/lib/format';
import { contractStatusTone } from '@/pages/contracts/contract-format';
import { humanize } from '@/pages/customers/detail/status-tones';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';

/* ── Constants ─────────────────────────────────────────────────────────── */

const PROCESS_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'in-progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'rejected', label: 'Rejected' },
];

const PROCESS_PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

/* ── Form types ────────────────────────────────────────────────────────── */

interface ProcessFormState {
  contract_process_title: string;
  contract_process_content: string;
  contract_process_status: string;
  contract_process_priority: string;
}

function blankProcessForm(): ProcessFormState {
  return {
    contract_process_title: '',
    contract_process_content: '',
    contract_process_status: 'pending',
    contract_process_priority: 'medium',
  };
}

interface DeliverFormState {
  contract_process_feedback: string;
  contract_process_feedback_status: string;
}

function blankDeliverForm(): DeliverFormState {
  return {
    contract_process_feedback: '',
    contract_process_feedback_status: 'completed',
  };
}

/**
 * Processes tab — the contract's workflow steps. Supports create (Drawer), delete
 * (ConfirmDialog), archive (ConfirmDialog), and deliver/feedback (Drawer).
 * The row shape is loose so every cell is read defensively.
 */
export function ProcessesTab({ id }: { id: number }) {
  const toast = useToast();
  const { data, isLoading, isError, refetch } = useContractProcesses(id);
  const rows = data?.results ?? [];

  // Create drawer
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<ProcessFormState>(blankProcessForm);
  const [createErrors, setCreateErrors] = useState<Partial<Record<keyof ProcessFormState, string>>>({});

  // Deliver drawer
  const [deliverTarget, setDeliverTarget] = useState<ContractProcessRow | null>(null);
  const [deliverForm, setDeliverForm] = useState<DeliverFormState>(blankDeliverForm);
  const [deliverErrors, setDeliverErrors] = useState<Partial<Record<keyof DeliverFormState, string>>>({});

  // Delete / archive confirms
  const [deleteTarget, setDeleteTarget] = useState<ContractProcessRow | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<ContractProcessRow | null>(null);

  const createMutation = useCreateContractProcess(id);
  const deleteMutation = useDeleteContractProcess(id);
  const archiveMutation = useArchiveContractProcess(id);
  const deliverMutation = useDeliverContractProcess(id);

  /* ── Create helpers ─────────────────────────────────────────────────── */

  const setCreateField = <K extends keyof ProcessFormState>(k: K, v: ProcessFormState[K]) => {
    setCreateForm((p) => ({ ...p, [k]: v }));
    setCreateErrors((p) => ({ ...p, [k]: undefined }));
  };

  const validateCreate = (): boolean => {
    const errs: Partial<Record<keyof ProcessFormState, string>> = {};
    if (!createForm.contract_process_title.trim()) errs.contract_process_title = 'Title is required.';
    setCreateErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleCreate = () => {
    if (!validateCreate()) return;
    createMutation.mutate(
      {
        contract: {
          contract_process_title: createForm.contract_process_title,
          contract_process_content: createForm.contract_process_content || undefined,
          contract_process_status: createForm.contract_process_status,
          contract_process_priority: createForm.contract_process_priority,
        },
      },
      {
        onSuccess: () => {
          toast.success('Process created');
          setCreateOpen(false);
          setCreateForm(blankProcessForm());
          setCreateErrors({});
        },
        onError: (err) => {
          const msg = err instanceof Error ? err.message : 'Failed to create process';
          toast.error('Failed to create process', { description: msg });
        },
      },
    );
  };

  /* ── Deliver helpers ─────────────────────────────────────────────────── */

  const setDeliverField = <K extends keyof DeliverFormState>(k: K, v: DeliverFormState[K]) => {
    setDeliverForm((p) => ({ ...p, [k]: v }));
    setDeliverErrors((p) => ({ ...p, [k]: undefined }));
  };

  const openDeliver = (row: ContractProcessRow) => {
    setDeliverTarget(row);
    setDeliverForm(blankDeliverForm());
    setDeliverErrors({});
  };

  const validateDeliver = (): boolean => {
    const errs: Partial<Record<keyof DeliverFormState, string>> = {};
    if (!deliverForm.contract_process_feedback.trim()) errs.contract_process_feedback = 'Feedback is required.';
    setDeliverErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleDeliver = () => {
    if (!deliverTarget || !validateDeliver()) return;
    const processId = deliverTarget.process_id;
    if (!processId) return;
    deliverMutation.mutate(
      {
        processId,
        payload: {
          contract_process_feedback: deliverForm.contract_process_feedback,
          contract_process_feedback_status: deliverForm.contract_process_feedback_status,
        },
      },
      {
        onSuccess: () => {
          toast.success('Feedback submitted');
          setDeliverTarget(null);
        },
        onError: (err) => {
          const msg = err instanceof Error ? err.message : 'Failed to submit feedback';
          toast.error('Failed to submit feedback', { description: msg });
        },
      },
    );
  };

  /* ── Delete / Archive helpers ────────────────────────────────────────── */

  const handleDelete = () => {
    if (!deleteTarget?.process_id) return;
    deleteMutation.mutate(deleteTarget.process_id, {
      onSuccess: () => {
        toast.success('Process cancelled');
        setDeleteTarget(null);
      },
      onError: (err) => {
        const msg = err instanceof Error ? err.message : 'Failed to cancel process';
        toast.error('Failed to cancel process', { description: msg });
        setDeleteTarget(null);
      },
    });
  };

  const handleArchive = () => {
    if (!archiveTarget?.process_id) return;
    archiveMutation.mutate(archiveTarget.process_id, {
      onSuccess: () => {
        toast.success('Process archived');
        setArchiveTarget(null);
      },
      onError: (err) => {
        const msg = err instanceof Error ? err.message : 'Failed to archive process';
        toast.error('Failed to archive process', { description: msg });
        setArchiveTarget(null);
      },
    });
  };

  /* ── Columns ─────────────────────────────────────────────────────────── */

  const columns = useMemo<ColumnDef<ContractProcessRow, unknown>[]>(
    () => [
      {
        id: 'process_code',
        header: 'Code',
        cell: ({ row }) => (
          <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>
            {row.original.process_code || `#${row.original.process_id ?? '—'}`}
          </span>
        ),
      },
      {
        id: 'process_name',
        header: 'Step',
        cell: ({ row }) => (
          <span style={{ overflowWrap: 'anywhere' }}>{row.original.process_name || '—'}</span>
        ),
      },
      {
        id: 'process_status',
        header: 'Status',
        cell: ({ row }) => {
          const status = row.original.process_status;
          if (!status) return '—';
          return (
            <StatusPill tone={contractStatusTone(status)}>{humanize(status)}</StatusPill>
          );
        },
      },
      {
        id: 'employee_name',
        header: 'Employee',
        cell: ({ row }) => row.original.employee_name || '—',
      },
      {
        id: 'created_at',
        header: 'Created',
        cell: ({ row }) => formatDateTime(row.original.created_at),
      },
      {
        id: '_actions',
        header: '',
        cell: ({ row }) => (
          <div style={{ display: 'flex', gap: 'var(--vn-space-1)', justifyContent: 'flex-end' }}>
            <Button
              size="sm"
              variant="ghost"
              aria-label="Add feedback"
              onClick={() => openDeliver(row.original)}
              disabled={!row.original.process_id}
            >
              <MessageSquare size={13} />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              aria-label="Archive process"
              onClick={() => setArchiveTarget(row.original)}
              disabled={!row.original.process_id}
            >
              <Archive size={13} />
            </Button>
            <Button
              size="sm"
              variant="danger"
              aria-label="Cancel process"
              onClick={() => setDeleteTarget(row.original)}
              disabled={!row.original.process_id}
            >
              <Trash2 size={13} />
            </Button>
          </div>
        ),
      },
    ],
    [],
  );

  /* ── Render ──────────────────────────────────────────────────────────── */

  if (isError) return <TabError title="Failed to load processes" onRetry={() => refetch()} />;
  if (isLoading) return <TabLoading />;

  return (
    <>
      <Toolbar
        style={{ marginBottom: 0 }}
        end={
          <Button
            size="sm"
            variant="primary"
            startIcon={<Plus size={14} />}
            onClick={() => {
              setCreateForm(blankProcessForm());
              setCreateErrors({});
              setCreateOpen(true);
            }}
          >
            Add process
          </Button>
        }
      />

      <DataTable
        columns={columns}
        data={rows}
        enableSorting={false}
        getRowId={(r, index) => String(r.process_id ?? r.process_code ?? index)}
        emptyState={
          <EmptyState
            title="No processes"
            description="No workflow steps for this contract yet."
          />
        }
      />

      {/* Create Drawer */}
      <Drawer open={createOpen} onOpenChange={(o) => { if (!o) setCreateOpen(false); }}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>Add process</DrawerTitle>
            <DrawerDescription>Create a new workflow step for this contract.</DrawerDescription>
          </DrawerHeader>
          <DrawerBody>
            <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
              <Field label="Title" required error={createErrors.contract_process_title}>
                {(fp) => (
                  <Input
                    {...fp}
                    value={createForm.contract_process_title}
                    onChange={(e) => setCreateField('contract_process_title', e.target.value)}
                    placeholder="Process step title"
                  />
                )}
              </Field>

              <Field label="Content" error={createErrors.contract_process_content}>
                {(fp) => (
                  <Textarea
                    {...fp}
                    rows={3}
                    value={createForm.contract_process_content}
                    onChange={(e) => setCreateField('contract_process_content', e.target.value)}
                    placeholder="Description or content for this step"
                  />
                )}
              </Field>

              <Field label="Status" error={createErrors.contract_process_status}>
                {(fp) => (
                  <Select
                    value={createForm.contract_process_status}
                    onValueChange={(v) => setCreateField('contract_process_status', v)}
                  >
                    <SelectTrigger {...fp}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PROCESS_STATUS_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>

              <Field label="Priority" error={createErrors.contract_process_priority}>
                {(fp) => (
                  <Select
                    value={createForm.contract_process_priority}
                    onValueChange={(v) => setCreateField('contract_process_priority', v)}
                  >
                    <SelectTrigger {...fp}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PROCESS_PRIORITY_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
            </div>
          </DrawerBody>
          <DrawerFooter>
            <Button variant="secondary" onClick={() => setCreateOpen(false)} disabled={createMutation.isPending}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleCreate} loading={createMutation.isPending}>
              Create
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* Deliver / Feedback Drawer */}
      <Drawer
        open={!!deliverTarget}
        onOpenChange={(o) => { if (!o) setDeliverTarget(null); }}
      >
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>Submit feedback</DrawerTitle>
            <DrawerDescription>
              Add feedback for:{' '}
              <strong>{deliverTarget?.process_name || `#${deliverTarget?.process_id}`}</strong>
            </DrawerDescription>
          </DrawerHeader>
          <DrawerBody>
            <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
              <Field label="Feedback" required error={deliverErrors.contract_process_feedback}>
                {(fp) => (
                  <Textarea
                    {...fp}
                    rows={4}
                    value={deliverForm.contract_process_feedback}
                    onChange={(e) => setDeliverField('contract_process_feedback', e.target.value)}
                    placeholder="Your feedback on this process step"
                  />
                )}
              </Field>

              <Field label="Update status to" error={deliverErrors.contract_process_feedback_status}>
                {(fp) => (
                  <Select
                    value={deliverForm.contract_process_feedback_status}
                    onValueChange={(v) => setDeliverField('contract_process_feedback_status', v)}
                  >
                    <SelectTrigger {...fp}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PROCESS_STATUS_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
            </div>
          </DrawerBody>
          <DrawerFooter>
            <Button
              variant="secondary"
              onClick={() => setDeliverTarget(null)}
              disabled={deliverMutation.isPending}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={handleDeliver} loading={deliverMutation.isPending}>
              Submit
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* Cancel (Delete) confirm */}
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}
        title="Cancel process"
        description={
          deleteTarget
            ? `Cancel process "${deleteTarget.process_name || `#${deleteTarget.process_id}`}"? This cannot be undone.`
            : ''
        }
        confirmLabel="Cancel process"
        destructive
        loading={deleteMutation.isPending}
        onConfirm={handleDelete}
      />

      {/* Archive confirm */}
      <ConfirmDialog
        open={!!archiveTarget}
        onOpenChange={(o) => { if (!o) setArchiveTarget(null); }}
        title="Archive process"
        description={
          archiveTarget
            ? `Archive process "${archiveTarget.process_name || `#${archiveTarget.process_id}`}"?`
            : ''
        }
        confirmLabel="Archive"
        loading={archiveMutation.isPending}
        onConfirm={handleArchive}
      />
    </>
  );
}
